import { useNavigate } from "react-router";
import { ArrowLeft } from "lucide-react";
import { Logo } from "../components/Logo";

export function PrivacyPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#F8F6F4] flex flex-col">

      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-white border-b border-[#E5E7EB]">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 h-14 flex items-center gap-4">
          <button onClick={() => navigate("/")} className="flex items-center gap-2.5 flex-shrink-0">
            <Logo className="w-10" />
            <span className="hidden sm:block text-sm font-semibold text-[#1F2933]">Leo Group</span>
          </button>
          <div className="flex-1" />
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-1.5 text-xs text-[#6B7280] hover:text-[#1F2933] border border-[#E5E7EB] hover:border-[#D1D5DB] px-3 py-1.5 rounded transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Back to Home
          </button>
        </div>
      </nav>

      {/* Content */}
      <main className="flex-1 max-w-4xl mx-auto w-full px-4 sm:px-8 py-12 sm:py-16">

        <div className="mb-10">
          <p className="text-xs font-semibold uppercase tracking-widest text-[#7B1A1A] mb-2">Legal</p>
          <h1 className="text-3xl sm:text-4xl font-bold text-[#1F2933] mb-3">Privacy Policy</h1>
          <p className="text-sm text-[#6B7280]">
            Last updated: 1 May 2026 &nbsp;·&nbsp; Compliant with the Protection of Personal Information Act 4 of 2013 (POPIA)
          </p>
        </div>

        <div className="bg-[#FFF7ED] border border-[#FED7AA] rounded-lg px-5 py-4 mb-8 text-sm text-[#92400E]">
          <strong>Your privacy matters.</strong> LEO Group is committed to protecting your personal information in accordance
          with POPIA and all applicable South African data protection legislation. This Policy explains what information we
          collect, why we collect it, how we use it, and your rights as a data subject.
        </div>

        <div className="prose prose-sm max-w-none space-y-8 text-[#374151]">

          <Section title="1. Responsible Party">
            <p>
              The Responsible Party (as defined in POPIA) for personal information processed through this Platform is:
            </p>
            <address className="not-italic mt-3 text-sm space-y-1 bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg px-4 py-3">
              <p><strong>LEO Group Holding Company (Pty) Ltd</strong></p>
              <p>Republic of South Africa</p>
              <p>Information Officer Email: <a href="mailto:privacy@leo-group.co.za" className="text-[#7B1A1A] hover:underline">privacy@leo-group.co.za</a></p>
            </address>
            <p className="mt-3">
              Our Information Officer has been designated in terms of <strong>Section 55 of POPIA</strong> and is responsible
              for ensuring compliance with this Policy and the Act.
            </p>
          </Section>

          <Section title="2. Personal Information We Collect">
            <p>We collect personal information only for the purposes described in this Policy. The categories of information we collect include:</p>
            <table className="w-full text-xs border border-[#E5E7EB] rounded-lg overflow-hidden mt-3">
              <thead className="bg-[#F9FAFB]">
                <tr>
                  <th className="text-left px-4 py-2 font-semibold text-[#374151] border-b border-[#E5E7EB]">Category</th>
                  <th className="text-left px-4 py-2 font-semibold text-[#374151] border-b border-[#E5E7EB]">Examples</th>
                  <th className="text-left px-4 py-2 font-semibold text-[#374151] border-b border-[#E5E7EB]">Purpose</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E5E7EB]">
                <tr>
                  <td className="px-4 py-2 text-[#374151]">Identity information</td>
                  <td className="px-4 py-2 text-[#6B7280]">First name, last name, company name</td>
                  <td className="px-4 py-2 text-[#6B7280]">Access control, NDA execution</td>
                </tr>
                <tr>
                  <td className="px-4 py-2 text-[#374151]">Contact information</td>
                  <td className="px-4 py-2 text-[#6B7280]">Email address, phone number</td>
                  <td className="px-4 py-2 text-[#6B7280]">Communication, access management</td>
                </tr>
                <tr>
                  <td className="px-4 py-2 text-[#374151]">Technical information</td>
                  <td className="px-4 py-2 text-[#6B7280]">IP address, browser type, session data</td>
                  <td className="px-4 py-2 text-[#6B7280]">Security, audit logging</td>
                </tr>
                <tr>
                  <td className="px-4 py-2 text-[#374151]">Consent records</td>
                  <td className="px-4 py-2 text-[#6B7280]">NDA signature, acceptance timestamp</td>
                  <td className="px-4 py-2 text-[#6B7280]">Legal compliance, audit trail</td>
                </tr>
                <tr>
                  <td className="px-4 py-2 text-[#374151]">Usage information</td>
                  <td className="px-4 py-2 text-[#6B7280]">Pages visited, session duration</td>
                  <td className="px-4 py-2 text-[#6B7280]">Platform security and improvement</td>
                </tr>
              </tbody>
            </table>
            <p className="mt-3">
              We do not collect special personal information (as defined in <strong>Section 26 of POPIA</strong>) such as
              health data, biometric data, religious or political beliefs, race, or criminal record, unless explicitly
              required and with your express consent.
            </p>
          </Section>

          <Section title="3. Lawful Basis for Processing">
            <p>
              We process personal information only where at least one of the following lawful bases under <strong>Section 11 of POPIA</strong> applies:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Consent:</strong> You have given specific, informed consent for the processing (e.g., submitting a stakeholder access request);</li>
              <li><strong>Contractual necessity:</strong> Processing is necessary for the performance of a contract to which you are a party (e.g., execution of an NDA);</li>
              <li><strong>Legitimate interests:</strong> Processing is necessary for the legitimate interests pursued by LEO Group, provided those interests are not overridden by your rights;</li>
              <li><strong>Legal obligation:</strong> Processing is required to comply with a legal obligation imposed on LEO Group;</li>
              <li><strong>Protection of your interests:</strong> Processing is necessary to protect your legitimate interests.</li>
            </ul>
          </Section>

          <Section title="4. How We Use Your Personal Information">
            <ul className="list-disc pl-5 space-y-1">
              <li>To process and manage stakeholder access requests;</li>
              <li>To generate and store executed Non-Disclosure Agreements;</li>
              <li>To manage access sessions and ensure platform security;</li>
              <li>To maintain audit logs for security and compliance purposes;</li>
              <li>To communicate with you regarding your access request or the Platform;</li>
              <li>To comply with applicable South African law and regulatory requirements;</li>
              <li>To investigate and prevent fraud, security incidents, or misuse of the Platform.</li>
            </ul>
            <p className="mt-3">
              We will not process your personal information for purposes incompatible with those listed above without
              obtaining your prior consent.
            </p>
          </Section>

          <Section title="5. Sharing of Personal Information">
            <p>
              LEO Group does not sell, rent, or trade your personal information to third parties. We may share your
              personal information only in the following circumstances:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Service providers:</strong> Trusted third-party operators (e.g., hosting, cloud storage, email delivery) who process data on our behalf under binding data processing agreements;</li>
              <li><strong>Legal requirements:</strong> Where disclosure is required by law, court order, or competent regulatory authority;</li>
              <li><strong>Protection of rights:</strong> Where necessary to protect the rights, property, or safety of LEO Group, its stakeholders, or the public.</li>
            </ul>
            <p className="mt-3">
              Any third-party operators are required to maintain the confidentiality and security of your information and
              are prohibited from using it for any purpose other than performing their contracted services.
            </p>
          </Section>

          <Section title="6. Cross-Border Transfers">
            <p>
              Where personal information is transferred to a recipient in a foreign country, LEO Group will ensure that
              such transfer complies with <strong>Section 72 of POPIA</strong>. We will only transfer personal information
              to jurisdictions that afford adequate levels of protection, or where appropriate safeguards (such as
              contractual clauses) are in place.
            </p>
          </Section>

          <Section title="7. Retention of Personal Information">
            <p>
              We retain personal information only for as long as is necessary to fulfil the purposes for which it was
              collected, or as required by applicable law. Our general retention periods are:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Access request records and NDAs:</strong> 5 years from date of signature (for legal and audit purposes);</li>
              <li><strong>Security and audit logs:</strong> 2 years;</li>
              <li><strong>Contact enquiries:</strong> 2 years from date of last interaction;</li>
              <li><strong>Session data:</strong> Deleted upon session expiry or at most 90 days.</li>
            </ul>
            <p className="mt-3">
              Once the retention period has expired, personal information is securely deleted or anonymised.
            </p>
          </Section>

          <Section title="8. Your Rights as a Data Subject">
            <p>
              In terms of <strong>Sections 23–25 of POPIA</strong>, you have the following rights regarding your personal
              information:
            </p>
            <ul className="list-disc pl-5 space-y-2">
              <li><strong>Right of access:</strong> You may request confirmation of whether LEO Group holds your personal information and obtain a copy thereof;</li>
              <li><strong>Right to correction:</strong> You may request correction of inaccurate, incomplete, or outdated personal information;</li>
              <li><strong>Right to deletion:</strong> You may request deletion or destruction of personal information that is no longer lawfully required to be retained;</li>
              <li><strong>Right to object:</strong> You may object, on reasonable grounds, to the processing of your personal information;</li>
              <li><strong>Right to withdraw consent:</strong> Where processing is based on consent, you may withdraw your consent at any time, without affecting the lawfulness of prior processing;</li>
              <li><strong>Right to complain:</strong> You have the right to lodge a complaint with the <strong>Information Regulator of South Africa</strong> (see Section 11 below).</li>
            </ul>
            <p className="mt-3">
              To exercise any of these rights, please contact our Information Officer at
              <a href="mailto:privacy@leo-group.co.za" className="text-[#7B1A1A] hover:underline ml-1">privacy@leo-group.co.za</a>.
              We will respond within 30 days of receiving a valid request.
            </p>
          </Section>

          <Section title="9. Security Safeguards">
            <p>
              LEO Group implements appropriate technical and organisational measures to protect your personal information
              against loss, theft, unauthorised access, disclosure, copying, use, or modification. These measures include:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Encrypted data transmission (TLS/HTTPS);</li>
              <li>Encryption of sensitive fields at rest (AES-256);</li>
              <li>Token-based access control with session expiry;</li>
              <li>Role-based access controls and audit logging;</li>
              <li>Regular security reviews and vulnerability assessments;</li>
              <li>Access restricted to authorised personnel on a need-to-know basis.</li>
            </ul>
            <p className="mt-3">
              In the event of a personal information breach that poses a risk to you, we will notify you and the
              Information Regulator as required by <strong>Section 22 of POPIA</strong>.
            </p>
          </Section>

          <Section title="10. Cookies and Tracking Technologies">
            <p>
              The Platform uses session storage and functional cookies strictly necessary for the operation of secure
              access sessions. We do not use third-party tracking cookies, advertising cookies, or behavioural analytics
              tools.
            </p>
            <p className="mt-3">
              You may configure your browser to refuse cookies; however, this may affect the functionality of the
              Platform, including the ability to maintain an authenticated session.
            </p>
          </Section>

          <Section title="11. Information Regulator">
            <p>
              If you are dissatisfied with our handling of your personal information, you have the right to lodge a
              complaint with the <strong>Information Regulator of South Africa</strong>:
            </p>
            <address className="not-italic mt-2 text-sm space-y-1 bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg px-4 py-3">
              <p><strong>Information Regulator (South Africa)</strong></p>
              <p>JD House, 27 Stiemens Street, Braamfontein, Johannesburg, 2001</p>
              <p>Email: <a href="mailto:inforeg@justice.gov.za" className="text-[#7B1A1A] hover:underline">inforeg@justice.gov.za</a></p>
              <p>Website: <a href="https://www.justice.gov.za/inforeg/" target="_blank" rel="noopener noreferrer" className="text-[#7B1A1A] hover:underline">www.justice.gov.za/inforeg</a></p>
            </address>
          </Section>

          <Section title="12. Changes to This Policy">
            <p>
              We may update this Privacy Policy from time to time. Any material changes will be reflected by updating the
              "Last updated" date at the top of this page. We encourage you to review this Policy periodically. Continued
              use of the Platform after changes constitutes acceptance of the updated Policy.
            </p>
          </Section>

          <Section title="13. Contact Us">
            <p>For any privacy-related queries, access requests, or concerns, contact our Information Officer:</p>
            <address className="not-italic mt-2 text-sm space-y-1">
              <p><strong>Information Officer — LEO Group Holding Company (Pty) Ltd</strong></p>
              <p>Republic of South Africa</p>
              <p>Email: <a href="mailto:privacy@leo-group.co.za" className="text-[#7B1A1A] hover:underline">privacy@leo-group.co.za</a></p>
            </address>
          </Section>

        </div>
      </main>

      <Footer />
    </div>
  );
}

// ─── Shared sub-components ────────────────────────────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h2 className="text-base font-semibold text-[#1F2933] mb-3 pb-2 border-b border-[#E5E7EB]">{title}</h2>
      <div className="space-y-3 text-sm leading-relaxed">{children}</div>
    </div>
  );
}

function Footer() {
  const navigate = useNavigate();
  return (
    <footer className="bg-[#6b1515] mt-16">
      <div className="max-w-5xl mx-auto px-4 sm:px-8 py-8">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Logo className="w-10" white />
            <span className="text-sm font-medium text-white">Leo Group</span>
          </div>
          <div className="flex items-center gap-4 text-xs text-white/60">
            <button onClick={() => navigate("/terms")} className="hover:text-white transition-colors">Terms</button>
            <button onClick={() => navigate("/privacy")} className="hover:text-white transition-colors">Privacy Policy</button>
            <button onClick={() => navigate("/compliance")} className="hover:text-white transition-colors">POPIA</button>
          </div>
        </div>
        <div className="border-t border-white/10 mt-6 pt-5 text-center text-xs text-white/30">
          © 2026 LEO Group Holding Company. All rights reserved.
        </div>
      </div>
    </footer>
  );
}
